/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type RefObject, useEffect, useRef, useState } from 'react';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import cx from 'classnames';
import NextImage from '@/components/NextImage';
import azCommonStyles from '@/theme/globals.module.scss';
import { useLabels } from '@/hooks/useLabels';
import { Button } from '@/components/Button';
import { AZButton } from '@/components/AZButton';
import { pageTypes } from '@/constants/page';
import { useHeaderData } from '@/features/header/api/getHeader';
import { getPreferredVehicle } from '@/features/header/utils/getPreferredVehicle';
import { getVehicleTypeIcon } from '@/features/ymme/utils/getVehicleTypeIcon';
import {
  type PopperProps,
  type PopperBodyProps,
  type PopperHeaderProps,
} from '@/components/AZCustomComponent/Popper/Popper';
import { YMMEModal } from '@/features/ymme/components/YMMEModal';
import styles from './styles.module.scss';
import { useAppState } from '@/stores/AppState';
import { saveUIState } from '@/stores/actions';
import dynamic from 'next/dynamic';
import { useRedirectAfterVehicleChanged } from '@/features/category/hooks/useRedirectAfterVehicleChanged';
import { useIsMobileQRExperience } from '@/hooks/useIsMobileQRExperience';
import { setInteractionLocation } from '@/features/ymme/utils/ymmeLocationHelper';

const vehicleIconSource = '/images/vehicle-new.svg';
const errorIconSource = '/images/notification/error-orange.svg';

const LazyPopper = dynamic<PopperProps>(() =>
  import('@/components/AZCustomComponent/Popper/Popper').then((mod) => mod.Popper),
);

const LazyPopperHeader = dynamic<PopperHeaderProps>(() =>
  import('@/components/AZCustomComponent/Popper/Popper').then((mod) => mod.PopperHeader),
);

const LazyPopperBody = dynamic<PopperBodyProps>(() =>
  import('@/components/AZCustomComponent/Popper/Popper').then((mod) => mod.PopperBody),
);

const labelMap = {
  lbladdvehicle: 'label_myvehicle',
  lblTooltipButtonLabelPdp: 'label_ymme_tooltip_body_button_pdp',
  lblTooltipButtonLabel: 'label_ymme_tooltip_body_button',
  lblYMMEaddvehicle: 'button_YMME_Body_addvehicle',
  lblYMMEAddYourVehicle: 'label_ymme_add_your_vehicle',
  lblTooltipBodyStart: 'label_ymme_tooltip_body_start',
  lblTooltipBodyEndPdp: 'label_ymme_tooltip_body_end_pdp',
  lblTooltipBodyEnd: 'label_ymme_tooltip_body_end',
};

type Props = {
  currentSize: 'mobile' | 'tablet' | 'desktop';
  dataTestIdDesktopVehicle?: string;
  isFixedHeader?: boolean;
  showTooltip?: boolean;
  tooltipAnchorEl?: RefObject<HTMLElement | null> | HTMLElement;
  vehicleText: string;
};

export const Vehicle = ({
  currentSize,
  dataTestIdDesktopVehicle,
  isFixedHeader = false,
  showTooltip = false,
  tooltipAnchorEl,
  vehicleText,
}: Props) => {
  const isQRMobileExperience = useIsMobileQRExperience();
  const router = useLegacyRouter();
  const { data: headerData } = useHeaderData();
  const vehicle = getPreferredVehicle(headerData);
  const [showYmme, setShowYmme] = useState(false);
  const [refocusVehicleBtn, setRefocusVehicleBtn] = useState(false);
  const [ymmeInteractionLocation, setYmmeInteractionLocation] = useState<string>(pageTypes.header);
  const vehicleBtn = useRef<HTMLButtonElement | null>(null);
  const vehicleBtnImage = useRef(null);
  const labels = useLabels(labelMap);
  const anchorEl = tooltipAnchorEl
    ? typeof tooltipAnchorEl === 'object' && 'current' in tooltipAnchorEl
      ? tooltipAnchorEl.current
      : tooltipAnchorEl
    : vehicleBtnImage.current;
  const isPDP = router.pathname === '/products/[...slug]';
  const buttonLabel = isPDP ? labels.lblTooltipButtonLabelPdp : labels.lblTooltipButtonLabel;
  const { dispatch: dispatchAppState } = useAppState();
  const isBrowsePageType = router.route === '/[...seoUrlPath]';
  const redirectAfterVehicleChanged = useRedirectAfterVehicleChanged();

  useEffect(() => {
    if (refocusVehicleBtn) {
      vehicleBtn.current?.focus();
    }
  }, [showYmme, refocusVehicleBtn]);

  const closeYMMETooltip = () => {
    dispatchAppState(saveUIState({ isYmmeTooltipEnabled: false }));
  };

  const handleOpenYmme = (interactionLocation: string) => {
    setShowYmme(true);
    setYmmeInteractionLocation(interactionLocation);
    setInteractionLocation(interactionLocation);
    vehicleBtn.current?.blur();
  };

  const closeYmme = () => {
    setShowYmme(false);
    setRefocusVehicleBtn(true);
  };

  let content = null;

  if (vehicleText) {
    const selectedVehicle = vehicleText.length > 50 ? vehicleText.slice(0, 50) : vehicleText;
    const vehicleIconWidth = isFixedHeader
      ? 20
      : currentSize == 'desktop' || currentSize == 'tablet'
        ? 27
        : 21;
    const vehicleIconHeight = isFixedHeader
      ? 20
      : currentSize == 'desktop' || currentSize == 'tablet'
        ? 23
        : 19;
    content = (
      <div
        data-testid="saved-vehicle-logo"
        className={cx(styles.savedVehicleAlign, {
          [styles.savedVehicleAlignSticky]: isFixedHeader,
        })}
      >
        <div
          className={cx(styles.vehicleIconContainer, {
            [styles.vehicleIconContainerSticky]: isFixedHeader,
          })}
        >
          <img
            src={getVehicleTypeIcon(vehicle?.vehicleTypeName, currentSize)}
            alt={labels.lbladdvehicle}
            width={vehicleIconWidth}
            height={vehicleIconHeight}
            decoding="async"
          />
        </div>
        <div
          data-testid="vehicle-text"
          className={cx(styles.vehicleDetails, {
            [styles.vehicleDetailsSticky]: isFixedHeader,
          })}
        >
          {selectedVehicle}
        </div>
        <div
          className={cx(styles.vehicleArrowContainer, {
            [styles.vehicleArrowContainerSticky]: isFixedHeader,
          })}
        >
          <img
            className={styles.header_arrow}
            src={'/images/header-arrow.svg'}
            alt=""
            width={isFixedHeader ? 6 : 8}
            height={isFixedHeader ? 11 : 12}
            decoding="async"
          />
        </div>
      </div>
    );
  } else {
    const addVehicleIconWidth = isFixedHeader ? 18 : currentSize === 'desktop' ? 22 : 20;

    const addVehicleIconHeight = isFixedHeader ? 14 : currentSize == 'desktop' ? 16 : 15;
    content = (
      <div
        className={cx(styles.vehicleAlign, {
          [styles.vehicleAlignSticky]: isFixedHeader,
        })}
      >
        <div
          ref={vehicleBtnImage}
          className={cx(styles.addVehicleIconContainer, {
            [styles.addVehicleIconContainerSticky]: isFixedHeader,
          })}
        >
          <img
            className={styles.vehicleIcon}
            src={vehicleIconSource}
            alt=""
            width={addVehicleIconWidth}
            height={addVehicleIconHeight}
            decoding="async"
          />
        </div>
        <div
          className={cx(azCommonStyles['az-body-2-regular'], styles.addVehicleText, {
            [styles.addVehicleTextSticky]: isFixedHeader,
          })}
          key="addVehicleBtn"
          data-testid="add-vehicle-header-btn"
        >
          {labels.lblYMMEaddvehicle}
        </div>
        <div
          className={cx(styles.vehicleArrowContainer, {
            [styles.vehicleArrowContainerSticky]: isFixedHeader,
          })}
        >
          <img
            className={styles.header_arrow}
            src={'/images/header-arrow.svg'}
            alt=""
            width={isFixedHeader ? 6 : 8}
            height={isFixedHeader ? 11 : 12}
            decoding="async"
          />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="vehicle-render">
      <Button
        data-testid={dataTestIdDesktopVehicle}
        onClick={() => handleOpenYmme(pageTypes.header)}
        className={cx(styles.vehicleButton, {
          [styles.vehicleButtonSticky]: isFixedHeader,
        })}
        disabled={showYmme}
        reference={vehicleBtn}
      >
        {content}
      </Button>
      <YMMEModal
        onUpdateVehicleSuccess={() => {
          if (isBrowsePageType) {
            void redirectAfterVehicleChanged();
          }
          closeYmme();
        }}
        showYmme={showYmme}
        closeYmmeWidget={closeYmme}
        pageType={ymmeInteractionLocation}
      />
      {anchorEl && !isQRMobileExperience && (
        <LazyPopper
          modifiers={{ arrow: { enabled: currentSize !== 'mobile' } }}
          strategy={isFixedHeader ? 'fixed' : 'absolute'}
          isOpen={!showYmme && showTooltip}
          anchorEl={anchorEl}
          classes={{
            popper: `${styles.ymmeTooltip} ${styles.ymmeTooltipRoot} ${
              isFixedHeader ? styles.fixedHeaderMargin : ''
            }`,
            paper: styles.ymmeTooltipPaper,
          }}
          placement={currentSize === 'mobile' ? 'bottom' : 'bottom-start'}
          role="presentation"
          elevation={4}
          hideForBots
        >
          <LazyPopperHeader
            handleClose={closeYMMETooltip}
            hideCloseIcon={false}
            classes={{
              title: styles.popperTitleWithoutBorder,
              heading: styles.popperHeaderText,
            }}
          >
            <div className={styles.ymmeTooltipHeader}>
              <NextImage
                src={errorIconSource}
                width={16}
                height={16}
                className={styles.addVehicleWarning}
                alt=""
              />
              <strong className={styles.addVehicleText}>
                {labels.lblYMMEAddYourVehicle.toLocaleUpperCase()}
              </strong>
            </div>
          </LazyPopperHeader>
          <LazyPopperBody>
            <div className={styles.ymmeTooltipBody}>
              {!isPDP && labels.lblTooltipBodyStart}
              <AZButton
                className={styles.openYmmeButton}
                variant="link"
                onPress={() => handleOpenYmme(pageTypes.ymmeAlert)}
                title={buttonLabel}
              />
              {isPDP ? labels.lblTooltipBodyEndPdp : labels.lblTooltipBodyEnd}
            </div>
          </LazyPopperBody>
        </LazyPopper>
      )}
    </div>
  );
};
